<template>
<div class="course">
    <div @click="closeOverlay" class="overlay-content" :class=" leftBar? 'active' : ''"></div>
    <class-topbar :breadcrumb="breadcrumb"/>
    <div class="d-flex">
        <class-menu
            v-model="leftBar"
            @input="v => leftBar = v"
            :certificate="certificate"
            :forum="forum"
            :lastContent="this.link"
        />
        <div class="content-container">
            <transition name="content-transition">
                <class-loading v-if="loading" expand />
            </transition>
            <class-navigation-buttons />
            <transition name="content-transition">
                <component
                    v-if="!loading"
                    v-model="content"
                    :is="contentItem.type"
                    @liveSettings="onChangeLiveSettings"
                    @side="toggleChat"
                    class="card current-component"
                />
            </transition>
            <transition name="content-transition">
                <tab-options v-if="showTabOptions && !loading"/>
            </transition>
        </div>
        <class-sidebar
            v-if="content.settings && content.settings.chat"
            v-model="rightBar"
            :showToggle="!rightBar"
            right
            @input="value => toggleChat({value})"
        >
            <chat-live
                :model-value="chat.value"
                :rightBar="rightBar"
                :disabled="chat.disabled"
                @liveSettings="onChangeLiveSettings"
            />
        </class-sidebar>
    </div>
    <class-info-modal/>
</div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';

import Video from './Class/Contents/ClassContentVideo';
import Upload from './Class/Contents/ClassContentUpload';
import Exercise from './Class/Contents/ClassContentExercise';
import Topic from './Class/Contents/ClassContentTopic';

import ChatLive from './Class/ClassChatLive';
import ProgressBar from './Common/ProgressBar';
import ClassNavigationButtons from './Class/ClassNavigationButtons';

import Tabs from '../argon/components/Tabs/Tabs';
import TabPane from '../argon/components/Tabs/Tab';
import BaseButton from '../argon/components/BaseButton';

import ClassLoading from './Class/ClassLoading';
import ClassSidebar from './Class/ClassSidebar';
import ClassMenu from './Class/ClassMenu';
import ClassTopbar from './Class/ClassTopbar';
import ClassInfoModal from './Class/ClassInfoModal';
import TabOptions from './Class/TabOptions/TabOptions';

export default {
    name: 'Class',

    components: {
        ClassLoading,
        ClassTopbar,
        ClassMenu,
        ClassSidebar,
        ClassNavigationButtons,
        ClassInfoModal,
        ChatLive,
        BaseButton,
        Exercise,
        Topic,
        Upload,
        ProgressBar,
        Video,
        Tabs,
        TabPane,
        TabOptions,
    },

    props: {
        course: Object,
        planId: Number,
        breadcrumb: String,
        certificate: Object,
        forum: Number,
    },

    data() {
        return {
            leftBar: true,
            rightBar: false,
            chat: {},
        };
    },

    computed: {
        ...mapGetters({
            language: 'LANGUAGE',
            contentItem: 'CLASS_CONTENT_ITEM',
            menuItems: 'CLASS_MENU_ITEMS',
            loading: 'CLASS_LOADING',
            content: 'CLASS_CONTENT',
            link: 'CLASS_LINK',
        }),

        windowWidth() { return document.documentElement.clientWidth; },

        showTabOptions() {
            if (this.content) {
                const hasSupport = !!this.content.files && !!this.content.files.length;
                const hasForum = !!this.content.forum;
                const hasDescription = !!this.content.lesson_translation;
                return (hasSupport || hasForum || hasDescription);
            } else if (this.content.type === 'video') {
                return true;
            }
            return false;
        },
    },

    methods: {
        ...mapActions({
            loadContent: 'CLASS_LOAD_CONTENT',
            loadMenuItems: 'CLASS_LOAD_MENU_ITEMS',
        }),

        ...mapMutations({ setCourse: 'SET_CLASS_COURSE' }),

        async getMenuItems() {
            await this.loadMenuItems({ planId: this.planId, courseId: this.course.id });
        },

        toggleChat({ value, data }) {
            // this.chat.component = data && data.chat ? component : false;
            this.chat.disabled = data && data.live ? 0 : 1;
            if (data && data.chat) {
                const chatType = data.chat_type || 'lv';
                const name = `NCH_${chatType.toUpperCase()}_${data.id}`;
                this.chat.value = {
                    room_id: data.id,
                    name,
                    status: data.chat,
                    history: data.chat_save,
                    moderate: data.chat_mod,
                    type: chatType,
                };
            }
            this.rightBar = value;
        },

        onChangeLiveSettings({ settings, room }) {
            this.chat = {
                ...this.chat,
                value: room,
                disabled: settings.live ? 0 : 1,
            };
        },

        closeOverlay() {
            this.leftBar = false;
        },
    },

    async beforeMount() {
        await this.getMenuItems();
        await this.loadContent({ iso: this.language.iso });
    },

    async mounted() {
        this.setCourse(this.course);
        if (this.windowWidth < 725) {
            this.leftBar = false;
            this.toggleChat({ value: false });
        }
    },
};
</script>

<style lang="sass" scoped>
.course
    position: relative

.content-container
    overflow-y: auto !important
    padding-top: 0
    padding-bottom: 0
    margin: 0 auto
    display: flex
    width: 100%
    flex-direction: column
    position: relative
    min-height: calc(100vh - 165px)

.content-transition-enter-active, .content-transition-leave-active
  transition: opacity .5s

.content-transition-enter, .content-transition-leave-to
  opacity: 0

.current-component
    display: flex
    margin-bottom: 0
    border-radius: 0
.current-description
    display: flex
    flex-grow: 1
    flex-direction: column
    @media (max-width: 1200px)
        width: 95%
        max-width: unset
.nch--content-upper-menu
    height: 55px

::-webkit-scrollbar
    width: 8px

::-webkit-scrollbar-track
    border-radius: 0

::-webkit-scrollbar-thumb
    margin: 2px
    border-radius: 10px
    background: rgba(0, 0, 0, 0.2)

.stretch
    height: 100%
    overflow: auto
    border-radius: 0
</style>

<style lang="sass">
.nch--navbar__fixed
    margin-top: 95px !important
</style>

<style scoped lang="scss">
@media (max-width: 1027px)
{
    .overlay-content {
        position: absolute;
        display: none;
        width: 100%;
        top: 70px;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 74;
        cursor: pointer;

        &.active {
            display: block !important;
        }
    }
}
</style>
